import { ref } from 'vue'
import eventBus from 'src/eventBus'

export function useKeyboard(callbacks = {}) {
  const keyboardHeight = ref(0)
  const isKeyboardVisible = ref(false)

  const onKeyboardWillShow = (info) => {
    if (info && info.keyboardHeight) {
      keyboardHeight.value = info.keyboardHeight
      isKeyboardVisible.value = true

      if (callbacks.onWillShow) {
        callbacks.onWillShow(info)
      }
    }
  }

  const onKeyboardDidShow = (info) => {
    if (info && info.keyboardHeight) {
      keyboardHeight.value = info.keyboardHeight
      isKeyboardVisible.value = true

      if (callbacks.onDidShow) {
        callbacks.onDidShow(info)
      }
    }
  }

  const onKeyboardWillHide = () => {
    keyboardHeight.value = 0
    isKeyboardVisible.value = false

    if (callbacks.onWillHide) {
      callbacks.onWillHide()
    }
  }

  const onKeyboardDidHide = () => {
    keyboardHeight.value = 0
    isKeyboardVisible.value = false

    if (callbacks.onDidHide) {
      callbacks.onDidHide()
    }
  }

  const scrollToElement = (elementRef, scrollAreaRef, options = {}) => {
    const {
      margin = 20,
      animationDuration = 200
    } = options

    if (elementRef && scrollAreaRef) {
      try {
        const inputElement = elementRef.$el || elementRef
        const inputRect = inputElement.getBoundingClientRect()
        const windowHeight = window.innerHeight
        const targetInputBottom = windowHeight - keyboardHeight.value - margin
        const currentInputBottom = inputRect.bottom
        const scrollOffset = currentInputBottom - targetInputBottom

        if (scrollOffset <= 0) {
          console.log('Element already visible above keyboard, no scroll needed')
          return
        }

        const currentScrollTop = scrollAreaRef.getScrollPosition().top
        const newScrollTop = currentScrollTop + scrollOffset

        scrollAreaRef.setScrollPosition('vertical', newScrollTop, animationDuration)

        console.log('Scrolled to element above keyboard', {
          keyboardHeight: keyboardHeight.value,
          windowHeight,
          targetInputBottom,
          currentInputBottom,
          scrollOffset,
          newScrollTop,
          currentScrollTop
        })
      } catch (error) {
        console.error('Error scrolling to element:', error)
        scrollAreaRef.setScrollPercentage('vertical', 0.95, animationDuration)
      }
    }
  }

  const restoreScrollPosition = (scrollAreaRef, options = {}) => {
    const {
      animationDuration = 150,
    } = options

    if (scrollAreaRef) {
      try {
        scrollAreaRef.setScrollPercentage('vertical', 1, animationDuration)
      } catch (error) {
        console.error('Error restoring scroll position:', error)
      }
    }
  }

  const getAvailableHeight = () => {
    return window.innerHeight - keyboardHeight.value
  }

  const isElementVisible = (elementRef, margin = 20) => {
    if (!elementRef) return false

    try {
      const element = elementRef.$el || elementRef
      const elementRect = element.getBoundingClientRect()
      const windowHeight = window.innerHeight
      const targetBottom = windowHeight - keyboardHeight.value - margin

      return elementRect.bottom <= targetBottom
    } catch (error) {
      console.error('Error checking element visibility:', error)
      return false
    }
  }

  const setupKeyboardListeners = () => {
    eventBus.$on('keyboardWillShow', onKeyboardWillShow)
    eventBus.$on('keyboardDidShow', onKeyboardDidShow)
    eventBus.$on('keyboardWillHide', onKeyboardWillHide)
    eventBus.$on('keyboardDidHide', onKeyboardDidHide)
  }

  const removeKeyboardListeners = () => {
    eventBus.$off('keyboardWillShow', onKeyboardWillShow)
    eventBus.$off('keyboardDidShow', onKeyboardDidShow)
    eventBus.$off('keyboardWillHide', onKeyboardWillHide)
    eventBus.$off('keyboardDidHide', onKeyboardDidHide)
  }

  return {
    keyboardHeight,
    isKeyboardVisible,

    scrollToElement,
    restoreScrollPosition,
    getAvailableHeight,
    isElementVisible,

    setupKeyboardListeners,
    removeKeyboardListeners
  }
}
